import React, {useMemo, useState} from 'react';
import {Box, Tab, Tabs} from '@mui/material';
import type {NoProps} from 'types';
import {CodeListsGate} from 'services/codelists';
import {ThesaurusesGate} from 'services/thesaurus';
import {Scene} from 'components';
import {
  useMultiThesaurusEditSceneExtensionCore,
  useThesaurusEditSceneExtensionCore,
} from 'sceneExtensions';
import {
  SceneExtensionsContext,
  useSceneExtensionsContext,
  useSceneExtensionsContextProviderValue,
} from 'sceneExtensions/contexts';
import {EditPage} from './EditPage';
import {PreviewPage} from './PreviewPage';
import {TablePage} from './TablePage';
import {demoCodeLists, demoThesauruses} from './demoSchema';

type Props = NoProps;

const pages = [
  {label: 'Edit', render: () => <EditPage />},
  {label: 'Preview', render: () => <PreviewPage />},
  {label: 'Table', render: () => <TablePage />},
] as const;

/**
 * Renders the scene shell with tabs. Reads the currently active scene
 * extension (e.g. the thesaurus editor) and renders it in the right panel.
 * Must be rendered inside `SceneExtensionsContext`.
 */
const SchemaDemoSceneContent: React.FC = () => {
  const [tab, setTab] = useState(0);
  const {currentExtension} = useSceneExtensionsContext();

  return (
    <Scene
      browserTitle={'Schema demo'}
      rightPanelConfig={currentExtension?.panelConfig}>
      <Scene.Header title={'Schema demo'} />
      <Scene.Content>
        <Box sx={{borderBottom: 1, borderColor: 'divider', mb: 2}}>
          <Tabs
            value={tab}
            onChange={(_event, value: number) => setTab(value)}
            aria-label="schema demo pages">
            {pages.map((page, index) => (
              <Tab key={page.label} label={page.label} id={`tab-${index}`} />
            ))}
          </Tabs>
        </Box>
        {pages[tab].render()}
      </Scene.Content>
      {currentExtension ? (
        <Scene.RightPanel>
          {currentExtension.renderRightPanel()}
        </Scene.RightPanel>
      ) : null}
    </Scene>
  );
};

/**
 * Demo scene illustrating example usage of `src/schema` and
 * `src/schemaDefinition`.
 *
 * A single schema (see `demoSchema.ts`) is rendered three ways, one per tab:
 * - Edit: `<DataForm />` editable form
 * - Preview: `<Preview />` read-only layout
 * - Table: `<Table />` list of entities
 *
 * Sets up the gates (codelists, thesauruses) and the scene extensions that the
 * schema components depend on, e.g. the thesaurus editor used by `thesaurus`
 * parts in edit mode.
 */
export const SchemaDemoScene: React.FC<Props> = () => {
  const thesaurusEditExtension = useThesaurusEditSceneExtensionCore();
  const multiThesaurusEditExtension = useMultiThesaurusEditSceneExtensionCore();
  const extensions = useMemo(
    () => [thesaurusEditExtension, multiThesaurusEditExtension],
    [thesaurusEditExtension, multiThesaurusEditExtension],
  );
  const sceneExtensionContext =
    useSceneExtensionsContextProviderValue(extensions);

  return (
    <CodeListsGate codeLists={demoCodeLists}>
      <ThesaurusesGate thesauruses={demoThesauruses}>
        <SceneExtensionsContext.Provider value={sceneExtensionContext}>
          <SchemaDemoSceneContent />
        </SceneExtensionsContext.Provider>
      </ThesaurusesGate>
    </CodeListsGate>
  );
};
